import type { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { DialogContextProvider } from '@xing-com/crate-entity-pages-common';
import { useEditContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { OmView } from '@xing-com/platform-layout-om';

import type {
  AboutUsSubpageFactsFragment_CompanyAboutUsFacts_Fragment,
  AboutUsSubpageFactsFragment_PublisherAboutUsFacts_Fragment,
} from '../../graphql/fragment/about-us-subpage-facts-fragment.gql-types';

import type { FactsItemProps } from './facts-item';
import { FactsItem } from './facts-item';
import * as Styled from './facts.styles';

export type FactsProps = {
  headlineKey: string;
  items: Array<FactsItemProps & { omViewContent?: JSX.Element | false }>;
  categoriesHeadlineKey: string;
  categories: string[];
  facts:
    | AboutUsSubpageFactsFragment_PublisherAboutUsFacts_Fragment
    | AboutUsSubpageFactsFragment_CompanyAboutUsFacts_Fragment;
};

export const Facts: FC<FactsProps> = ({
  headlineKey,
  items,
  categoriesHeadlineKey,
  categories,
}) => {
  const { isEditing } = useEditContext();
  const { pageContext } = usePageContext();

  if (!(items?.length > 0 || (categories && categories?.length > 0))) {
    return null;
  }

  return (
    <div>
      <Styled.FactsContainer>
        {items?.length > 0 && (
          <>
            <div>
              <Styled.FactsHeadline size={'large'} sizeWide={'xxlarge'}>
                <FormattedMessage
                  id={headlineKey}
                  defaultMessage={headlineKey}
                />
              </Styled.FactsHeadline>
              {isEditing && (
                <DialogContextProvider>
                  <Styled.FactsEditButton
                    size={'small'}
                    showIcon={true}
                    to={`${pageContext.basePath}/${pageContext.pageSlug}/edit/facts`}
                    data-testid="edit-facts"
                  />
                </DialogContextProvider>
              )}
            </div>
            <Styled.FactsList>
              {items.map(({ omViewContent, ...props }, index) =>
                omViewContent ? (
                  <li key={index}>
                    <OmView
                      trigger={(activateOmView) => (
                        <FactsItem {...props} onClick={activateOmView} />
                      )}
                    >
                      {omViewContent}
                    </OmView>
                  </li>
                ) : (
                  <li key={index}>
                    <FactsItem {...props} />
                  </li>
                )
              )}
            </Styled.FactsList>
          </>
        )}
        {categories && categories.length > 0 && (
          <>
            <Styled.CategoriesHeadline size={'large'} sizeWide={'xxlarge'}>
              <FormattedMessage
                id={categoriesHeadlineKey}
                defaultMessage={categoriesHeadlineKey}
              />
            </Styled.CategoriesHeadline>
            {isEditing && (
              <DialogContextProvider>
                <Styled.FactsEditButton
                  size={'small'}
                  showIcon={true}
                  to={`${pageContext.basePath}/${pageContext.pageSlug}/edit/facts?type=industries`}
                  data-testid="edit-categories"
                />
              </DialogContextProvider>
            )}
          </>
        )}
        <Styled.CategoriesList data-testid="categories-list">
          {categories?.map((item: string) => (
            <li key={item}>
              <Styled.FactsTag>{item}</Styled.FactsTag>
            </li>
          ))}
        </Styled.CategoriesList>
      </Styled.FactsContainer>
    </div>
  );
};
